// LivePreviewPanel (#262) — the Looks editor's "Live camera" controls. Connects to the camera's live
// Stream feed (via useLivePreview's relay popup) and draws the dithered frames the worker posts back onto
// the editor's EXISTING orbs: the Look orb (edited palette) + the Dither·Colour orb (reference palette).
// Slider/palette edits appear on the real higher-res feed in real time. Controls-only — no canvas of its
// own; it draws onto the canvas refs LookPortal hands it, so the Live source lives inside the normal layout.
import { useCallback, useEffect, useState } from "react";
import { useLivePreview } from "./useLivePreview";
import type { EditableLook } from "./LookPortal";

const QUALITIES = [
  { name: "SVGA", p: 0, dim: "800×600" },
  { name: "XGA", p: 1, dim: "1024×768" },
  { name: "UXGA", p: 2, dim: "1600×1200" },
  { name: "QXGA", p: 3, dim: "2048×1536" },
];
const GRIDS = [
  { name: "Chunky", w: 128, hint: "128px · big blocky pixels" },
  { name: "Pixie", w: 240, hint: "240px · the camera's own screen" },
  { name: "Fine", w: 360, hint: "360px · more detail, less chunky" },
  { name: "Native", w: 0, hint: "full frame · barely pixel-art" },
];
const HOST_KEY = "pixie.live.host";
const Q_KEY = "pixie.live.quality";
const G_KEY = "pixie.live.grid";

const loadIdx = (key: string, def: number, max: number): number => {
  try { const n = parseInt(localStorage.getItem(key) || "", 10); return n >= 0 && n < max ? n : def; } catch { return def; }
};

export interface LivePreviewPanelProps {
  /** The editor's existing orb canvases the worker's frames are drawn onto. */
  lookCanvasRef: { current: HTMLCanvasElement | null };
  ditherCanvasRef: { current: HTMLCanvasElement | null };
  look: EditableLook;
  /** The Dither·Colour reference Look (colour-cube / grayscale + adjustments), or null to skip that orb. */
  refLook: EditableLook | null;
  cameraName?: string;
}

export function LivePreviewPanel({ lookCanvasRef, ditherCanvasRef, look, refLook, cameraName }: LivePreviewPanelProps): React.JSX.Element {
  // Draw each posted bitmap onto the matching orb (nearest-neighbour, so the pixel-art stays crisp).
  const onPaint = useCallback((role: "look" | "dither", bmp: ImageBitmap) => {
    const cv = role === "look" ? lookCanvasRef.current : ditherCanvasRef.current;
    if (cv) {
      if (cv.width !== bmp.width || cv.height !== bmp.height) { cv.width = bmp.width; cv.height = bmp.height; }
      const ctx = cv.getContext("2d");
      if (ctx) { ctx.imageSmoothingEnabled = false; ctx.drawImage(bmp, 0, 0); }
    }
    bmp.close();
  }, [lookCanvasRef, ditherCanvasRef]);

  const { state, detail, fps, kbps, connect, disconnect, setLook, setRef, setGrid } = useLivePreview({ onPaint });

  const [host, setHost] = useState(() => { try { return localStorage.getItem(HOST_KEY) || "pixie.local"; } catch { return "pixie.local"; } });
  const [qualityIdx, setQualityIdx] = useState(() => loadIdx(Q_KEY, 1, QUALITIES.length));
  const [gridIdx, setGridIdx] = useState(() => loadIdx(G_KEY, 1, GRIDS.length));

  // Push the Look-under-edit + the Dither·Colour reference + the grid to the worker as they change.
  useEffect(() => { setLook(look); }, [look, setLook]);
  useEffect(() => { setRef(refLook); }, [refLook, setRef]);
  useEffect(() => { setGrid(GRIDS[gridIdx]!.w); }, [gridIdx, setGrid]);

  const onConnect = useCallback(() => {
    try { localStorage.setItem(HOST_KEY, host); } catch { /* private mode */ }
    connect(host, QUALITIES[qualityIdx]!.p);
  }, [host, qualityIdx, connect]);

  const onQuality = (i: number) => {
    setQualityIdx(i);
    try { localStorage.setItem(Q_KEY, String(i)); } catch { /* private mode */ }
    if (state === "live" || state === "connecting") connect(host, QUALITIES[i]!.p); // reconnect at the new source res
  };
  const onGridPick = (i: number) => {
    setGridIdx(i);
    try { localStorage.setItem(G_KEY, String(i)); } catch { /* private mode */ } // the grid effect posts it to the worker
  };

  const dot = state === "live" ? "●" : state === "error" ? "●" : "○";
  return (
    <div className={`lookportal__live-panel lookportal__live-panel--${state}`} data-testid="live-panel">
      <span className="lookportal__live-lbl">Camera</span>
      <div className="lookportal__live-ctl">
        <input
          type="text" value={host} onChange={(e) => setHost(e.target.value)} placeholder="pixie.local"
          aria-label="camera address" data-testid="live-host" className="lookportal__live-host"
        />
        <button type="button" className="lookportal__btn" onClick={onConnect} data-testid="live-connect">
          {state === "idle" || state === "error" ? "Connect" : "Reconnect"}
        </button>
        {state !== "idle" && (
          <button type="button" className="lookportal__btn" onClick={disconnect} data-testid="live-disconnect">Stop</button>
        )}
      </div>

      <span className="lookportal__live-lbl" title="The resolution the camera streams — sharper source, more bandwidth.">Quality</span>
      <div className="lookportal__live-ctl" data-testid="live-quality">
        {QUALITIES.map((q, i) => (
          <button key={q.name} type="button" title={q.dim}
            className={`lookportal__btn${i === qualityIdx ? " is-active" : ""}`} aria-pressed={i === qualityIdx}
            onClick={() => onQuality(i)}>{q.name}</button>
        ))}
      </div>

      <span className="lookportal__live-lbl" title="The pixel-art grid — how chunky the dithered pixels are. Independent of stream quality.">Pixels</span>
      <div className="lookportal__live-ctl" data-testid="live-pixels">
        {GRIDS.map((g, i) => (
          <button key={g.name} type="button" title={g.hint}
            className={`lookportal__btn${i === gridIdx ? " is-active" : ""}`} aria-pressed={i === gridIdx}
            onClick={() => onGridPick(i)}>
            {g.name}<span className="lookportal__dim"> {g.w ? `${g.w}px` : "full"}</span>
          </button>
        ))}
      </div>

      <p className="lookportal__live-status" aria-live="polite" data-testid="live-state">
        <span className="lookportal__live-dot" aria-hidden="true">{dot}</span>{" "}
        {state === "idle" && `Put ${cameraName || "the camera"} in Stream mode, then Connect.`}
        {state === "connecting" && (detail || "connecting…")}
        {state === "live" && (<>Live<span aria-hidden="true"> · {fps} fps · {kbps} KB/s</span></>)}
        {state === "error" && (detail || "error")}
      </p>
    </div>
  );
}
